import {
  FRIENDSHIP_FLOOR,
  type FriendshipTier,
  GIFTS_PER_DAY,
  GIFT_POINTS,
  TALK_POINTS,
  tierFor,
} from '@/data/friendship';
import type { ItemId } from '@/data/items';
import { NPCS, type NpcDefinition, type NpcId } from '@/data/npcs';

/**
 * What everyone thinks of you.
 *
 * Saved, like weapon growth and for the same reason: it is a history the
 * player made and is meant to keep. Unlike almost everything else here it
 * cannot be regenerated from a seed — there is no function from the world to
 * how well somebody knows you.
 */

export type GiftReaction = 'liked' | 'neutral' | 'disliked';

export interface GiftOutcome {
  readonly reaction: GiftReaction;
  readonly points: number;
  /** What they say about it. */
  readonly line: string;
  /** The tier reached, when this gift moved them up one. */
  readonly promotedTo: string | null;
}

interface Standing {
  points: number;
  /** Day index of the last gift, so generosity is paced rather than bulk. */
  lastGiftDay: number;
  /** And of the last conversation, so turning up counts once. */
  lastTalkDay: number;
}

export class Friendships {
  private readonly standings = new Map<NpcId, Standing>();

  private standing(id: NpcId): Standing {
    let current = this.standings.get(id);
    if (!current) {
      current = { points: 0, lastGiftDay: -1, lastTalkDay: -1 };
      this.standings.set(id, current);
    }
    return current;
  }

  pointsFor(id: NpcId): number {
    return this.standing(id).points;
  }

  tierFor(id: NpcId): FriendshipTier {
    return tierFor(this.pointsFor(id));
  }

  /**
   * Credit standing directly.
   *
   * For the ways of pleasing someone that are neither a chat nor a gift —
   * filling a request off the board, and whatever a quest decides is worth
   * something. Returns the tier reached when this crossed one, so the caller
   * can say so.
   */
  add(id: NpcId, points: number): FriendshipTier | null {
    const standing = this.standing(id);
    const before = tierFor(standing.points);

    standing.points = Math.max(FRIENDSHIP_FLOOR, standing.points + points);

    const after = tierFor(standing.points);
    return after.name === before.name ? null : after;
  }

  /** Whether they will take a gift today. */
  canReceiveGift(id: NpcId, dayIndex: number): boolean {
    return this.standing(id).lastGiftDay !== dayIndex || GIFTS_PER_DAY > 1;
  }

  /**
   * Credit a conversation, once per day.
   *
   * Returns true when it counted, so the caller can stay quiet about the
   * second and third chat rather than reporting nothing repeatedly.
   */
  greet(id: NpcId, dayIndex: number): boolean {
    const standing = this.standing(id);
    if (standing.lastTalkDay === dayIndex) return false;

    standing.lastTalkDay = dayIndex;
    this.add(id, TALK_POINTS);
    return true;
  }

  reactionTo(npc: NpcDefinition, item: ItemId): GiftReaction {
    if (npc.likes.includes(item)) return 'liked';
    if (npc.dislikes.includes(item)) return 'disliked';
    return 'neutral';
  }

  /**
   * Hand something over.
   *
   * The caller removes the item; this only decides what it was worth. Keeping
   * the inventory out of here means "what would this be worth" can be asked
   * without giving anything away.
   */
  give(npc: NpcDefinition, item: ItemId, dayIndex: number): GiftOutcome {
    const reaction = this.reactionTo(npc, item);
    const points = GIFT_POINTS[reaction];

    const promoted = this.add(npc.id, points);
    this.standing(npc.id).lastGiftDay = dayIndex;

    return {
      reaction,
      points,
      line:
        reaction === 'liked'
          ? npc.onLiked
          : reaction === 'disliked'
            ? npc.onDisliked
            : npc.onNeutral,
      promotedTo: promoted?.name ?? null,
    };
  }

  toSave(): Record<string, Standing> {
    return Object.fromEntries(this.standings);
  }

  static fromSave(value: unknown): Friendships {
    const friendships = new Friendships();
    if (!value || typeof value !== 'object') return friendships;

    for (const [id, standing] of Object.entries(value as Record<string, unknown>)) {
      if (!(id in NPCS)) continue;

      const raw = standing as Partial<Standing>;
      const points = Math.floor(Number(raw.points));
      if (!Number.isFinite(points)) continue;

      friendships.standings.set(id as NpcId, {
        points: Math.max(FRIENDSHIP_FLOOR, points),
        lastGiftDay: Number.isFinite(Number(raw.lastGiftDay)) ? Number(raw.lastGiftDay) : -1,
        lastTalkDay: Number.isFinite(Number(raw.lastTalkDay)) ? Number(raw.lastTalkDay) : -1,
      });
    }

    return friendships;
  }
}
